import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { setListingStatus } from '../../api/objects-write'
import type { ListingStatus } from '../../api/objects-write'
import { t } from '../../i18n'

/**
 * Three buttons where there used to be ten statuses and an evidence form.
 *
 * The verification machinery — who checked it, with what evidence, valid until
 * when, and a task raised when it runs out — was built for listings held from
 * somebody else, where a flat really can be let out from under us. The agency
 * lets its own flats. Asking the person with the keys to fill in a form proving
 * their own flat exists, and then withdrawing it on a timer when they do not,
 * was the panel arguing with the only source of truth there is.
 *
 * So: are we offering it, is it let, or are we not offering it just now. Those
 * are the three things that happen, and each is one click.
 *
 * The distinction between the last two is not cosmetic and is the reason there
 * are three buttons rather than a switch. "Let" means somebody is living there,
 * so it also stops the flat around it being offered and the places inside it
 * (docs/adr/0010); "not offering" is a decision about this space alone and
 * leaves its neighbours alone. The other seven statuses still exist underneath
 * for imports and for later, and nobody has to look at them.
 */
export function OfferSwitch({
  listingID,
  status,
  propertyID,
  blocked,
}: {
  readonly listingID: string
  readonly status: string
  readonly propertyID: string
  /** What is in the way, if anything: a let room stops the flat around it. */
  readonly blocked: readonly string[]
}) {
  const [asking, setAsking] = useState<ListingStatus | null>(null)
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const client = useQueryClient()

  const move = useMutation({
    mutationFn: (next: ListingStatus) => setListingStatus(listingID, next, reason.trim()),
    onSuccess: () => {
      setAsking(null)
      setReason('')
      setFailure(null)
      void client.invalidateQueries({ queryKey: ['property', propertyID] })
    },
    onError: (error: Error) => setFailure(error.message),
  })

  const choices: readonly { value: ListingStatus; label: string; tone: string }[] = [
    { value: 'verified', label: t('offer.on'), tone: 'good' },
    { value: 'rented', label: t('offer.rented'), tone: 'taken' },
    { value: 'withdrawn', label: t('offer.off'), tone: 'off' },
  ]

  // Everything that is not one of the three reads as "not being offered", which
  // is what it means to somebody looking at the screen.
  const current = choices.some((choice) => choice.value === status) ? status : 'withdrawn'

  if (asking !== null) {
    return (
      <form
        className="build-form"
        onSubmit={(event) => {
          event.preventDefault()
          move.mutate(asking)
        }}
      >
        <div className="field">
          <label className="field__label" htmlFor={`offer-reason-${listingID}`}>
            {t('offer.reason')}
          </label>
          <input
            id={`offer-reason-${listingID}`}
            className="field__input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={
              asking === 'rented' ? t('offer.reasonRented') : t('offer.reasonOff')
            }
            autoFocus
          />
          <p className="field__hint">{t('offer.reasonHint')}</p>
        </div>

        {failure === null ? null : <Alert tone="error">{failure}</Alert>}

        <div className="build-form__actions">
          <Button type="submit" busy={move.isPending}>
            {t('offer.confirm')}
          </Button>
          <Button variant="secondary" onClick={() => setAsking(null)} disabled={move.isPending}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    )
  }

  const inTheWay = blocked.length > 0

  return (
    <div className="offer-switch">
      <span className="offer-switch__question">{t('offer.question')}</span>

      {choices.map((choice) => (
        <button
          key={choice.value}
          type="button"
          className={[
            'offer-switch__choice',
            `offer-switch__choice--${choice.tone}`,
            current === choice.value ? 'offer-switch__choice--current' : '',
          ]
            .filter((one) => one !== '')
            .join(' ')}
          aria-pressed={current === choice.value}
          disabled={move.isPending || current === choice.value || (inTheWay && choice.value === 'verified')}
          title={inTheWay && choice.value === 'verified' ? t('offer.blockedShort') : undefined}
          onClick={() => {
            // Switching it on needs no explanation. Switching it off does, and
            // the sentence somebody types is worth more in a month than any list
            // of codes they would have picked from to get past the form.
            if (choice.value === 'verified') {
              move.mutate(choice.value)
              return
            }
            setAsking(choice.value)
          }}
        >
          {choice.label}
        </button>
      ))}

      {inTheWay ? (
        <span className="offer-switch__blocked">{t('offer.blockedShort')}</span>
      ) : null}

      {failure === null ? null : <Alert tone="error">{failure}</Alert>}
    </div>
  )
}
